import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Mail, Send, Loader2, Search, X, ArrowLeft } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';

// ponytail: 200 rows/page — supabase range pagination, grow on demand via Load More
const PAGE_SIZE = 200;

interface SentEmail {
  id: string;
  recipient_email: string;
  recipient_name: string | null;
  sender_email: string | null;
  subject: string | null;
  body: string | null;
  body_preview: string | null;
  sent_at: string;
  source: 'compose' | 'campaign';
  campaign_id: string | null;
}

export function SentEmailsList() {
  const [emails, setEmails] = useState<SentEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState<SentEmail | null>(null);
  const [search, setSearch] = useState('');
  const [recipientFilter, setRecipientFilter] = useState<string>('all');
  const [senderFilter, setSenderFilter] = useState<string>('all');
  const { currentWorkspace } = useWorkspaces();
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const wsIdRef = useRef<string | undefined>(currentWorkspace?.id);

  const fetchSentEmails = useCallback(async () => {
    const wsId = currentWorkspace?.id;
    if (!wsId) {
      setEmails([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    loadingMoreRef.current = false;
    setLoadingMore(false);
    try {
      const [countRes, pageRes] = await Promise.all([
        supabase
          .from('sent_emails')
          .select('id', { count: 'exact', head: true })
          .eq('workspace_id', wsId),
        supabase
          .from('sent_emails')
          .select('id, recipient_email, recipient_name, sender_email, subject, body, body_preview, sent_at, source, campaign_id')
          .eq('workspace_id', wsId)
          .order('sent_at', { ascending: false })
          .range(0, PAGE_SIZE - 1),
      ]);

      // Stale guard: workspace switched while these were in flight.
      if (wsIdRef.current !== wsId) return;

      if (countRes.error) {
        console.error('Error counting sent emails:', countRes.error);
      }
      if (pageRes.error) {
        console.error('Error fetching sent emails:', pageRes.error);
      }

      const pageRows = (pageRes.data as SentEmail[]) ?? [];
      setEmails(pageRows);
      // Count failure → fall back to loaded count so the view never crashes.
      setTotalCount(countRes.error ? pageRows.length : (countRes.count ?? 0));
    } catch (error) {
      console.error('Error fetching sent emails:', error);
    } finally {
      if (wsIdRef.current === wsId) setLoading(false);
    }
  }, [currentWorkspace?.id]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    if (emails.length >= totalCount) return;
    const wsId = currentWorkspace?.id;
    if (!wsId) return;

    const from = emails.length;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const { data, error } = await supabase
        .from('sent_emails')
        .select('id, recipient_email, recipient_name, sender_email, subject, body, body_preview, sent_at, source, campaign_id')
        .eq('workspace_id', wsId)
        .order('sent_at', { ascending: false })
        .range(from, from + PAGE_SIZE - 1);

      // Stale guard: workspace switched mid-loadMore — drop the page, don't append.
      if (wsIdRef.current !== wsId) return;

      if (error) {
        console.error('Error loading more sent emails:', error);
        return;
      }
      if (data) {
        setEmails(prev => {
          const seen = new Set(prev.map(e => e.id));
          const merged = [...prev];
          for (const row of data as SentEmail[]) {
            if (!seen.has(row.id)) merged.push(row);
          }
          return merged;
        });
      }
    } catch (error) {
      console.error('Error loading more sent emails:', error);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [currentWorkspace?.id, emails.length, totalCount]);

  useEffect(() => {
    wsIdRef.current = currentWorkspace?.id;
    fetchSentEmails();
  }, [fetchSentEmails]);

  // Unique recipients and senders for filter dropdowns
  const uniqueRecipients = useMemo(() => {
    const set = new Set(emails.map(e => e.recipient_email).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [emails]);

  const uniqueSenders = useMemo(() => {
    const set = new Set(emails.map(e => e.sender_email).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [emails]);

  // Filtered emails
  const filteredEmails = useMemo(() => {
    let result = emails;

    if (recipientFilter !== 'all') {
      result = result.filter(e => e.recipient_email === recipientFilter);
    }
    if (senderFilter !== 'all') {
      result = result.filter(e => e.sender_email === senderFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        (e.recipient_email?.toLowerCase().includes(q)) ||
        (e.recipient_name?.toLowerCase().includes(q)) ||
        (e.sender_email?.toLowerCase().includes(q)) ||
        (e.subject?.toLowerCase().includes(q)) ||
        (e.body_preview?.toLowerCase().includes(q))
      );
    }

    return result;
  }, [emails, recipientFilter, senderFilter, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="text-center py-12">
        <Send className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium">
          {totalCount === 0 ? 'No sent emails yet' : 'Could not load sent emails'}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {totalCount === 0 ? 'Emails you send will appear here.' : 'Try again later.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full -m-8">
      {/* Left panel: list */}
      <div className={cn(
        "flex flex-col border-r",
        selectedEmail ? "w-[400px] flex-shrink-0" : "flex-1"
      )}>
        {/* Header + search */}
        <div className="p-4 space-y-3">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Sent</h2>
            <p className="text-sm text-muted-foreground">
              {filteredEmails.length} of {totalCount} email{totalCount !== 1 ? 's' : ''}
              {totalCount > emails.length && (
                <span className="text-muted-foreground/70">{' '}· showing first {emails.length}</span>
              )}
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search sent emails..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-2.5"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <Select value={recipientFilter} onValueChange={setRecipientFilter}>
              <SelectTrigger className="h-8 text-xs flex-1">
                <SelectValue placeholder="Recipient" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All recipients</SelectItem>
                {uniqueRecipients.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {uniqueSenders.length > 0 && (
              <Select value={senderFilter} onValueChange={setSenderFilter}>
                <SelectTrigger className="h-8 text-xs flex-1">
                  <SelectValue placeholder="Sender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All senders</SelectItem>
                  {uniqueSenders.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Email list */}
        <div className="flex-1 overflow-y-auto">
          {filteredEmails.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No emails match your search
            </div>
          ) : (
            filteredEmails.map((email) => (
              <button
                key={email.id}
                onClick={() => setSelectedEmail(email)}
                className={cn(
                  "w-full text-left flex items-center gap-3 px-4 py-3 border-b hover:bg-accent/5 transition-colors",
                  selectedEmail?.id === email.id && "bg-accent/10"
                )}
              >
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">
                      {email.recipient_name || email.recipient_email}
                    </span>
                    <Badge
                      variant={email.source === 'campaign' ? 'secondary' : 'outline'}
                      className="text-[10px] flex-shrink-0 px-1 py-0"
                    >
                      {email.source === 'campaign' ? 'Campaign' : 'Compose'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {email.subject || '(No subject)'}
                  </p>
                  {!selectedEmail && email.body_preview && (
                    <p className="text-xs text-muted-foreground/70 truncate mt-0.5">
                      {email.body_preview}
                    </p>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                  {formatDistanceToNow(new Date(email.sent_at), { addSuffix: true })}
                </div>
              </button>
            ))
          )}
          {emails.length < totalCount && (
            <div className="p-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Load more ({totalCount - emails.length} remaining)
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Right panel: preview */}
      {selectedEmail && (
        <div className="flex-1 flex flex-col min-w-0">
          {/* Preview header */}
          <div className="p-4 border-b space-y-2">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setSelectedEmail(null)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h3 className="text-lg font-semibold truncate">
                {selectedEmail.subject || '(No subject)'}
              </h3>
            </div>
            <div className="flex flex-col gap-1 text-sm text-muted-foreground pl-9">
              {selectedEmail.sender_email && (
                <div>
                  <span className="font-medium text-foreground">From:</span>{' '}
                  {selectedEmail.sender_email}
                </div>
              )}
              <div>
                <span className="font-medium text-foreground">To:</span>{' '}
                {selectedEmail.recipient_name
                  ? `${selectedEmail.recipient_name} <${selectedEmail.recipient_email}>`
                  : selectedEmail.recipient_email}
              </div>
              <div>
                <span className="font-medium text-foreground">Date:</span>{' '}
                {format(new Date(selectedEmail.sent_at), 'MMM d, yyyy h:mm a')}
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant={selectedEmail.source === 'campaign' ? 'secondary' : 'outline'}
                  className="text-xs"
                >
                  {selectedEmail.source === 'campaign' ? 'Campaign' : 'Compose'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Email body - sanitized with DOMPurify */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 p-6">
            {selectedEmail.body ? (
              <div
                className="prose prose-sm max-w-none w-full break-words [&_img]:max-w-full [&_table]:max-w-full"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(selectedEmail.body)
                }}
              />
            ) : selectedEmail.body_preview ? (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {selectedEmail.body_preview}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                No email content available
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
